import { useRef } from 'react';
import { useRcPortalWindowContext } from '../../contexts';
import { getRefElement } from '../../utils';
import { useEventCallback } from '../useEventCallback';
/**
 * Provide a utils for switch trigger touch event or mouse event between different device
 *
 * - Trigger `touch` event in `touch support` device
 *   - onTouchStart
 *   - onTouchEnd
 * - Trigger `mouse` event in `non touch support` device
 *   - onMouseDown
 *   - onMouseUp
 *
 * ### Once that is touch support device, that mouse event will never can be trigger
 * https://developer.mozilla.org/en-US/docs/Web/API/Touch_events/Supporting_both_TouchEvent_and_MouseEvent
 *
 * Event order:
 * - touchstart
 * - Zero or more touchmove events, depending on movement of the finger(s)
 * - touchend
 * - mousemove
 * - mousedown
 * - mouseup
 * - click
 */
export var useTouchMouseEvent = function (_a, _b) {
    var onMouseDownArg = _a.onMouseDown, onMouseUpArg = _a.onMouseUp, onTouchStartArg = _a.onTouchStart, onTouchEndArg = _a.onTouchEnd;
    var actionRef = (_b === void 0 ? {} : _b).actionRef;
    var stopMouseEventRef = useRef(false);
    var document = useRcPortalWindowContext().document;
    var onTouchStart = useEventCallback(function (e) {
        stopMouseEventRef.current = true;
        onTouchStartArg === null || onTouchStartArg === void 0 ? void 0 : onTouchStartArg(e);
    });
    function checkInside(elm) {
        var hostElement = getRefElement(actionRef);
        if (hostElement) {
            return !!(elm && isElmEqualOrContain(elm, hostElement));
        }
        return false;
    }
    var onTouchEnd = useEventCallback(function (e) {
        var isInside;
        if (actionRef && e.touches.length < 2 && e.changedTouches.length < 2) {
            var touch = e.touches[0] || e.changedTouches[0];
            var elm = document.elementFromPoint(touch.clientX, touch.clientY);
            isInside = !!elm && checkInside(elm);
        }
        onTouchEndArg === null || onTouchEndArg === void 0 ? void 0 : onTouchEndArg(e, isInside);
    });
    var onMouseDown = useEventCallback(function (e) {
        if (stopMouseEventRef.current) {
            return;
        }
        onMouseDownArg === null || onMouseDownArg === void 0 ? void 0 : onMouseDownArg(e);
    });
    var onMouseUp = useEventCallback(function (e) {
        var isInside;
        if (stopMouseEventRef.current)
            return;
        if (actionRef) {
            var elm = e.target;
            isInside = !!elm && checkInside(elm);
        }
        onMouseUpArg === null || onMouseUpArg === void 0 ? void 0 : onMouseUpArg(e, isInside);
    });
    return {
        onTouchStart: onTouchStart,
        onTouchEnd: onTouchEnd,
        onMouseDown: onMouseDown,
        onMouseUp: onMouseUp,
    };
};
/**
 * check is element equal or contains target element
 */
export var isElmEqualOrContain = function (sourceTarget, containTarget) {
    var elm = getRefElement(sourceTarget);
    var target = getRefElement(containTarget);
    return elm && target && (target === elm || target.contains(elm));
};
